<template>
  <div class="dismissal-screen">
    <div class="header">
      <div class="title">{{ schoolName }} - {{ screenName }}</div>

      <!-- 中间滚动显示正在放学的班级 -->
      <div class="header-marquee" v-if="dismissingClassesStr">
        <div class="marquee-content">
          <span class="marquee-label">正在放学：</span>
          <span class="marquee-text">{{ dismissingClassesStr }}</span>
        </div>
      </div>

      <div class="time">{{ currentTime }}</div>
    </div>

    <!-- 顶栏统计 -->
    <div class="stats-bar">
      <div class="stat-item">
        <div class="label">今日放学班级</div>
        <div class="value">{{ stats.totalClasses }}</div>
      </div>
      <div class="stat-item highlight">
        <div class="label">正在放学</div>
        <div class="value warning">{{ stats.dismissing }}</div>
      </div>
      <div class="stat-item">
        <div class="label">已放学</div>
        <div class="value success">{{ stats.finished }}</div>
      </div>
      <div class="stat-item">
        <div class="label">{{ detentionTitle }}</div>
        <div class="value danger">{{ stats.detained }}</div>
      </div>
    </div>

    <div class="main-content">
      <!-- 左侧：班级放学状态 -->
      <div class="class-grid-section" ref="gridSection" @mouseenter="pauseGridScroll" @mouseleave="resumeGridScroll">
        <div class="grid-scroll-wrapper" :style="gridScrollStyle" ref="gridWrapper">
          <div v-for="(grade, gIdx) in displayGradeData" :key="gIdx" class="grade-group">
            <div class="section-title">
              {{ grade.GradeName }}
              <span class="grade-time" v-if="getGradeTime(grade)">{{ getGradeTime(grade) }}</span>
            </div>
            <div class="class-grid">
              <div v-for="cls in grade.Classes" :key="cls.ClassId" class="class-card" :class="statusClass(cls.Status)">
                <div class="card-header">
                  <div class="header-left">
                    <div class="class-name">{{ cls.ClassName }}</div>

                    <!-- 放学时间显示：移到班级名下方 -->
                    <div class="class-time-header"
                      :style="{ visibility: (cls.DismissalTime && cls.DismissalTime.trim() !== '' && !cls.DismissalTime.includes('未设置')) ? 'visible' : 'hidden' }">
                      放学: {{ (cls.DismissalTime && cls.DismissalTime.trim() !== '' &&
                        !cls.DismissalTime.includes('未设置'))
                        ? cls.DismissalTime : '00:00' }}
                    </div>
                  </div>
                  <div class="status-badge">{{ statusText(cls.Status) }}</div>
                </div>

                <div class="card-body">
                  <div class="info-row">
                    <span>班额: {{ cls.TotalCount }}</span>
                    <span>已走: {{ cls.LeftCount }}</span>
                  </div>
                  <!-- 底部只保留统计数字 -->
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="gradeData.length === 0" class="empty-tip"
          style="flex:1; text-align:center; color:#ccc; margin-top:50px;">
          暂无班级数据
        </div>
      </div>

      <!-- 右侧：留堂/晚放学详情 -->
      <div class="side-panel">
        <div class="section-title">{{ detentionTitle }}明细</div>
        <div class="detention-list" ref="detentionList" @mouseenter="pauseScroll" @mouseleave="resumeScroll">
          <div class="scroll-wrapper" :style="scrollStyle" ref="scrollWrapper">
            <div class="detention-item" v-for="(student, index) in displayRetainedList" :key="index">
              <div class="student-info">
                <div class="s-name">{{ student.StudentName }}</div>
                <div class="s-class" :class="'s-status-' + student.Status">{{ student.ClassName }}</div>
              </div>
            </div>
          </div>
          <div v-if="retainedList.length === 0" class="empty-tip">
            暂无留堂学生
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBigScreenData } from '../api/modules/big-screen';

export default {
  name: "DismissalBigScreen",
  data() {
    return {
      currentTime: "",
      timer: null,
      dataTimer: null,
      schoolName: "智慧校园",
      screenName: "",
      // 按年级分组的班级数据
      gradeData: [],
      // 统计数据
      stats: {
        totalClasses: 0,
        dismissing: 0,
        finished: 0,
        retained: 0, // 滞留学生 (老师勾选)
      },
      // 右侧留堂详情列表
      retainedList: [],
      // 滚动激活标志
      retainedScrollActive: false,
      gridScrollActive: false,
      loading: false,
      // 滚动相关
      scrollY: 0,
      scrollTimer: null,
      isPaused: false,
      // 左侧网格滚动相关
      gridScrollY: 0,
      gridScrollTimer: null,
      isGridPaused: false,
    };
  },
  computed: {
    scrollStyle() {
      return {
        transform: `translateY(-${this.scrollY}px)`,
        transition: this.scrollY === 0 ? 'none' : 'transform 0.1s linear'
      };
    },
    gridScrollStyle() {
      return {
        transform: `translateY(-${this.gridScrollY}px)`,
        transition: this.gridScrollY === 0 ? 'none' : 'transform 0.1s linear'
      };
    },
    // 动态标题
    detentionTitle() {
      return "滞留学生";
    },
    // 将所有班级扁平化用于计算或调试（如果需要）
    allClasses() {
      let list = [];
      this.gradeData.forEach((g) => {
        if (g.Classes) {
          list = list.concat(g.Classes);
        }
      });
      return list;
    },
    // 将原有列表复制一份用于无缝滚动
    displayRetainedList() {
      // 只有在确定需要无缝滚动时才复制一份，否则会看到重复数据
      if (this.retainedScrollActive && this.retainedList.length > 0) {
        return [...this.retainedList, ...this.retainedList];
      }
      return this.retainedList;
    },
    // 将原有年级数据复制一份用于无缝滚动
    displayGradeData() {
      // 左侧网格也遵循按需倍增原则，防止单年级时出现重复
      if (this.gridScrollActive && this.gradeData.length > 0) {
        return [...this.gradeData, ...this.gradeData];
      }
      return this.gradeData;
    },
    // 获取正在放学的班级名称字符串
    // 获取正在放学的班级名称字符串
    dismissingClassesStr() {
      const list = [];
      this.gradeData.forEach((g) => {
        if (g.Classes) {
          g.Classes.forEach((c) => {
            if (c.Status === 1) { // 1-Dismissing
              list.push(c.ClassName);
            }
          });
        }
      });
      if (list.length === 0) return "";
      return list.join("   ·   "); // 用点号分隔
    }
  },
  mounted() {
    this.updateTime();
    this.timer = setInterval(this.updateTime, 1000);
    this.fetchData();
    // 5秒轮询一次
    this.dataTimer = setInterval(this.fetchData, 5000);
    this.startScroll();
    this.startGridScroll();
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (this.dataTimer) clearInterval(this.dataTimer);
    this.stopScroll();
    this.stopGridScroll();
  },
  methods: {
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleString("zh-CN", { hour12: false });
    },
    getGradeTime(grade) {
      if (grade.Classes && grade.Classes.length > 0) {
        return grade.Classes[0].ExpectedTime;
      }
      return "";
    },
    statusClass(status) {
      if (status === 1) return "card-dismissing"; // 1-正在放学
      if (status === 2) return "card-finished"; // 2-已放学
      return "card-pending"; // 0-未放学
    },
    statusText(status) {
      const map = {
        0: "未放学",
        1: "放学中",
        2: "已放学",
      };
      return map[status] || "未放学";
    },
    fetchData() {
      const code = this.$route.query.code || "";

      this.loading = true;
      getBigScreenData({ CODE: code })
        .then((res) => {
          if (res.data.success) {
            const data = res.data.response;
            this.schoolName = data.SchoolName;
            this.screenName = data.ScreenName;
            // this.currentTime = data.CurrentTime; // 可选

            // 映射统计数据
            if (data.Stats) {
              this.stats = {
                totalClasses: data.Stats.TotalClasses,
                dismissing: data.Stats.DismissingClasses,
                finished: data.Stats.DismissedClasses,
                retained: data.Stats.RetainedStudentsCount,
                detained: data.Stats.RetainedStudentsCount
              };
            }

            // 映射年级班级数据
            this.gradeData = data.GradeData || [];

            this.$nextTick(() => {
              this.checkScrollActive();
            });

            // 映射右侧详情
            const newRetainedList = data.RetainedStudentsList || [];
            // 简单对比，如果数据一样就不赋值，避免触发重绘和滚动重置
            if (JSON.stringify(this.retainedList) !== JSON.stringify(newRetainedList)) {
              this.retainedList = newRetainedList;

              // 数据变更后，检查是否需要开启滚动
              this.$nextTick(() => {
                this.checkScrollActive();
              });
            }
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    startScroll() {
      if (this.scrollTimer) clearInterval(this.scrollTimer);
      this.scrollTimer = setInterval(() => {
        if (this.isPaused || !this.retainedScrollActive) return;

        if (this.$refs.scrollWrapper && this.$refs.detentionList) {
          const wrapperHeight = this.$refs.scrollWrapper.clientHeight;
          this.scrollY += 1;

          // wrapperHeight 此时已经是双倍高度，所以滚动到一半（原始高度）时重置
          if (this.scrollY >= wrapperHeight / 2) {
            this.scrollY = 0;
          }
        }
      }, 50); // 20fps
    },
    checkScrollActive() {
      if (this.$refs.detentionList && this.$refs.scrollWrapper) {
        const containerHeight = this.$refs.detentionList.clientHeight;
        const wrapper = this.$refs.scrollWrapper;
        const currentWrapperHeight = wrapper.offsetHeight || wrapper.scrollHeight;
        const contentHeight = this.retainedScrollActive ? currentWrapperHeight / 2 : currentWrapperHeight;
        this.retainedScrollActive = contentHeight > containerHeight;
        if (!this.retainedScrollActive) this.scrollY = 0;
      }
      if (this.$refs.gridSection && this.$refs.gridWrapper) {
        const containerHeight = this.$refs.gridSection.clientHeight;
        const wrapper = this.$refs.gridWrapper;
        const currentWrapperHeight = wrapper.offsetHeight || wrapper.scrollHeight;
        const contentHeight = this.gridScrollActive ? currentWrapperHeight / 2 : currentWrapperHeight;
        this.gridScrollActive = contentHeight > containerHeight;
        if (!this.gridScrollActive) this.gridScrollY = 0;
      }
    },
    stopScroll() {
      if (this.scrollTimer) clearInterval(this.scrollTimer);
    },
    pauseScroll() {
      this.isPaused = true;
    },
    resumeScroll() {
      this.isPaused = false;
    },
    // 左侧网格滚动逻辑
    startGridScroll() {
      if (this.gridScrollTimer) clearInterval(this.gridScrollTimer);
      this.gridScrollTimer = setInterval(() => {
        // 虽然数据是倍增的，但只有高度超出时才真正执行 transform 滚动
        if (this.isGridPaused) return;

        if (this.$refs.gridWrapper && this.$refs.gridSection) {
          const containerHeight = this.$refs.gridSection.clientHeight;
          const wrapperHeight = this.$refs.gridWrapper.clientHeight;

          // 如果总内容（的一半）都不如容器高，则不滚动
          if (wrapperHeight / 2 <= containerHeight) {
            this.gridScrollY = 0;
            this.gridScrollActive = false;
            return;
          }

          this.gridScrollActive = true;
          this.gridScrollY += 1;

          if (this.gridScrollY >= wrapperHeight / 2) {
            this.gridScrollY = 0;
          }
        }
      }, 50);
    },
    stopGridScroll() {
      if (this.gridScrollTimer) clearInterval(this.gridScrollTimer);
    },
    pauseGridScroll() {
      this.isGridPaused = true;
    },
    resumeGridScroll() {
      this.isGridPaused = false;
    }
  },
};
</script>

<style scoped>
/* 基础容器 - 深色科技风 */
.dismissal-screen {
  width: 100vw;
  height: 100vh;
  background-color: #0f172a;
  /* 深蓝灰色，比纯黑更有质感 */
  color: #f1f5f9;
  font-family: "PingFang SC", "Microsoft YaHei", "Inter", sans-serif;
  /* 优先中文优化 */
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 头部 */
.header {
  height: 80px;
  background: rgba(30, 41, 59, 0.8);
  /* 稍微通透一点 */
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.title {
  font-size: 32px;
  font-weight: bold;
  letter-spacing: 2px;
  background: linear-gradient(to right, #60a5fa, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.time {
  font-size: 28px;
  color: #ffffff;
  font-family: monospace;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  min-width: 250px;
  text-align: right;
}

/* 跑马灯效果 */
.header-marquee {
  flex: 1;
  margin: 0 40px;
  height: 50px;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 25px;
  border: 1px solid rgba(245, 158, 11, 0.3);
  display: flex;
  align-items: center;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.2);
}

.marquee-content {
  display: inline-block;
  white-space: nowrap;
  padding-left: 100%;
  /* 初始位置在最右侧外 */
  animation: marquee-scroll 20s linear infinite;
  color: #fbbf24;
  font-size: 22px;
  font-weight: bold;
}

.marquee-label {
  color: #f59e0b;
  margin-right: 10px;
}

.marquee-text {
  color: #fef3c7;
}

/* 鼠标放上去暂停滚动，方便查看 */
.header-marquee:hover .marquee-content {
  animation-play-state: paused;
}

@keyframes marquee-scroll {
  0% {
    transform: translate(0, 0);
  }

  100% {
    transform: translate(-100%, 0);
  }
}

/* 统计栏 */
.stats-bar {
  display: flex;
  justify-content: space-around;
  padding: 20px 40px;
  background-color: #0f172a;
  /* 纯深色背景 */
}

.stat-item {
  text-align: center;
  flex: 1;
  margin: 0 10px;
  padding: 15px;
  background: #1e293b;
  /* 纯色背景 */
  border-radius: 12px;
  border: 2px solid #334155;
  /* 强化边框 */
}

.stat-item.highlight {
  background: #1e3a8a;
  /* 深蓝色强化对比 */
  border-color: #3b82f6;
}

.label {
  font-size: 18px;
  color: #ffffff;
  /* 纯白高对比度 */
  margin-bottom: 8px;
  font-weight: 700;
  /* 加粗 */
}

.value {
  font-size: 42px;
  /* 加大 */
  font-weight: 900;
  /* 加粗 */
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.value.warning {
  color: #f59e0b;
}

.value.success {
  color: #10b981;
}

.value.danger {
  color: #ef4444;
}

/* 主内容区 */
.main-content {
  flex: 1;
  display: flex;
  padding: 10px 40px;
  /* 减小顶部内边距 30px -> 10px */
  gap: 30px;
  /* 减小左右间距 40px -> 30px */
  overflow: hidden;
}

.class-grid-section {
  flex: 3;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* 隐藏原生滚动条，使用 JS 模拟滚动 */
  padding-right: 20px;
  /* 滚动条位置防遮挡 */
  padding-bottom: 20px;
  /* 底部留白 */
  position: relative;
}

.grid-scroll-wrapper {
  width: 100%;
}

.class-grid {
  /* 去掉 flex: 1 和 overflow，让它自然撑开，由外层 class-grid-section 控制滚动 */
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  /* 再次缩小最小宽度 210px -> 200px */
  /* 缩小最小宽度，一行能放更多 */
  grid-auto-rows: min-content;
  gap: 10px;
  /* 减小网格间距 12px -> 10px */
}

.section-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 20px;
  padding-left: 15px;
  border-left: 4px solid #3b82f6;
  color: #e2e8f0;
}

.grade-group {
  margin-bottom: 20px;
  /* 减小年级间距 40px -> 20px */
}

.grade-time {
  font-size: 30px;
  margin-left: 20px;
  color: #facc15;
  /* 更亮的高亮黄 */
  font-weight: bold;
  text-shadow: 0 0 5px rgba(250, 204, 21, 0.3);
}

/* 班级卡片 */
.class-card {
  background: #1e293b;
  /* 统一基础底色，不要太黑 */
  border-radius: 8px;
  /* 圆角稍微改小一点更紧凑 */
  padding: 10px 14px;
  /* 稍微增加一点内边距让文字不那么挤 */
  border: 2px solid transparent;
  /* 预留边框位置 */
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
  /* 加深阴影增加立体感 */
  min-height: 160px;
  /* 给一个最小高度，保证卡片整齐 */
  justify-content: space-between;
}

@keyframes pulse-yellow {
  0% {
    box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.4);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(234, 179, 8, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(234, 179, 8, 0);
  }
}

.card-dismissing {
  /* 实色黄色 - 放学中 */
  background: #f59e0b;
  border-color: #d97706;
  animation: pulse-yellow 2s infinite;
}

.card-dismissing .class-name,
.card-dismissing .class-body,
.card-dismissing .info-row,
.card-dismissing .class-time-header {
  color: #0f172a !important;
  /* 高对比度深色文字 */
}

.card-finished {
  /* 实色绿色 - 已放学 */
  background: #10b981;
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.card-finished .class-name,
.card-finished .class-body,
.card-finished .info-row,
.card-finished .class-time-header {
  color: #ffffff !important;
  /* 高对比度白色文字 */
}

.card-pending {
  /* 实色红色 - 未放学 */
  background: #ef4444;
  border-color: #dc2626;
}

.card-pending .class-name,
.card-pending .class-body,
.card-pending .info-row,
.card-pending .class-time-header {
  color: #ffffff !important;
  /* 高对比度白色文字 */
}

.card-header {
  display: flex;
  flex-direction: column;
  /* 改为垂直排列 */
  justify-content: center;
  align-items: center;
  /* 居中对齐 */
  margin-bottom: 8px;
  flex: 1;
  gap: 12px;
  /* 增加间距 */
  text-align: center;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  width: 100%;
}

.class-name {
  font-size: 32px;
  /* 更大 */
  font-weight: 800;
  color: #f8fafc;
  letter-spacing: 1px;
}

.dismissal-time-row {
  margin-top: 5px;
  font-size: 20px;
  color: #cbd5e1;
  text-align: right;
  font-weight: bold;
}

.class-time {
  font-size: 16px;
  color: #94a3b8;
  /* 次要信息回归灰色，形成层级 */
  font-weight: 500;
}

.status-badge {
  /* position: absolute; removed to fix overlap */
  padding: 6px 14px;
  border-radius: 9999px;
  /* 全圆角 */
  font-size: 15px;
  /* 加大 */
  font-weight: 800;
  white-space: nowrap;
  flex-shrink: 0;
}

.card-dismissing .status-badge {
  background: #ffffff;
  color: #f59e0b;
  border: 1px solid #d97706;
}

.card-finished .status-badge {
  background: #ffffff;
  color: #10b981;
  border: 1px solid #059669;
}

.card-pending .status-badge {
  background: #ffffff;
  color: #ef4444;
  border: 1px solid #dc2626;
}

/* 班级卡片内的字体 */
.class-name {
  font-size: 32px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.5px;
  line-height: 1.2;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  /* 兼容标准属性，消除警告 */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: center;
  /* 文字强制居中 */
}

/* 紧凑布局修正 */
.card-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0px;
  gap: 12px;
  /* 增加标题、时间与标签之间的间距 */
  text-align: center;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 20px;
  font-weight: 800;
  white-space: nowrap;
  flex-shrink: 0;
  height: fit-content;
}

.card-body {
  margin-top: 4px;
  /* 减小整体上移后的间距 */
  font-size: 20px;
  font-weight: 700;
  color: #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.class-time-header {
  font-size: 18px;
  color: #fbbf24;
  margin-top: 2px;
  font-weight: bold;
  height: 20px;
  /* 固定高度占位 */
}

.info-row {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 0px;
  padding-bottom: 0;
  border-bottom: none;
}

.detained-info {
  margin-top: 10px;
  background: rgba(239, 68, 68, 0.1);
  padding: 10px;
  border-radius: 6px;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.detained-label {
  color: #ef4444;
  font-weight: 900;
  /* 加粗 */
  margin-bottom: 4px;
}

.detained-names {
  color: #fca5a5;
  font-size: 14px;
  font-weight: 700;
  word-break: break-all;
}

/* 侧边栏 */
.side-panel {
  flex: 1;
  background: #0f172a;
  /* 更深的纯色背景 */
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  border: 2px solid #334155;
  /* 强化边框 */
  max-width: 300px;
}

.detention-list {
  flex: 1;
  overflow: hidden;
  /* 隐藏滚动条，手动控制 */
  padding-right: 0;
  position: relative;
}

.scroll-wrapper {
  /* 滚动容器 */
  width: 100%;
}

/* 留堂列表样式优化 */
.detention-item {
  background: #1e293b;
  /* 纯色背景 */
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  border-left: 6px solid #ef4444;
  /* 强化左侧警告色 */
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 1px solid #334155;
  border-right: 1px solid #334155;
  border-bottom: 1px solid #334155;
}

.student-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
  padding-bottom: 6px;
  margin-bottom: 2px;
}

.s-name {
  color: #ffffff;
  font-size: 24px;
  /* 姓名加大 */
  font-weight: 800;
}

.s-class {
  font-size: 16px;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 800;
  background: #334155;
  /* 默认底色 */
  color: #ffffff;
}

.s-status-0 {
  background: #ef4444;
  /* 红色背景 - 未放学 */
  color: #ffffff;
}

.s-status-1 {
  background: #f59e0b;
  /* 黄色背景 - 放学中 */
  color: #0f172a;
  /* 深色文字保持高对比度 */
}

.s-status-2 {
  background: #10b981;
  /* 绿色背景 - 已放学 */
  color: #ffffff;
}

.parent-info {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 14px;
  color: #cbd5e1;
}

.p-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.p-row i {
  color: #64748b;
  /* 图标颜色 */
  width: 16px;
  text-align: center;
}

.reason {
  background: rgba(239, 68, 68, 0.1);
  color: #fca5a5;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: 4px;
}

.tag-status {
  text-align: right;
  font-weight: bold;
  margin-top: -20px;
  /* 稍微往上提一点，或者单独放一行 */
  margin-bottom: 4px;
}

.empty-tip {
  text-align: center;
  color: #64748b;
  margin-top: 50px;
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 6px;
  /* 调细 */
  height: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.5);
  /* 轨道半透明 */
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: rgba(71, 85, 105, 0.6);
  /* 滚动条深色半透明 */
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(99, 102, 241, 0.5);
  /* 悬停变色 */
}
</style>
